/**
 * Position Snapping
 *
 * Factory function for reducing position jitter by snapping to a grid.
 */

import {Position} from "../../types/position";

export interface SnapConfig {
  threshold: number;
}

export const SNAP_CONFIG: SnapConfig = {
  threshold: 0.3,
};

// ─── Factory function (preferred) ────────────────────────────────────────────

export interface PositionSnapCacheHandle {
  snap(position: Position, id: string): Position;
  clear(id: string): void;
  clearAll(): void;
}

export function createPositionSnapCache(
  threshold = SNAP_CONFIG.threshold
): PositionSnapCacheHandle {
  const cache = new Map<string, Position>();

  function snap(position: Position, id: string): Position {
    const cached = cache.get(id);

    if (!cached || (cached.x === 0 && cached.y === 0 && cached.z === 0)) {
      cache.set(id, {...position});
      return position;
    }

    const dx = position.x - cached.x;
    const dy = position.y - cached.y;
    const dz = position.z - cached.z;
    const movedDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (movedDistance > threshold) {
      cache.set(id, {...position});
      return position;
    }

    return cached;
  }

  return {
    snap,
    clear: (id) => {
      cache.delete(id);
    },
    clearAll: () => {
      cache.clear();
    },
  };
}

// ─── Standalone helper ───────────────────────────────────────────────────────

export function snapPositionSimple(
  position: Position,
  gridSize: number = 0.1
): Position {
  return {
    x: Math.round(position.x / gridSize) * gridSize,
    y: Math.round(position.y / gridSize) * gridSize,
    z: Math.round(position.z / gridSize) * gridSize,
  };
}
